import { Component, Input, OnInit } from '@angular/core';
import { DeliveryPackage } from '../../model/deliveryPackage';
import { HttpErrorResponse } from '@angular/common/http';
import { FormGroup, FormControl } from '@angular/forms';
import { forkJoin, Observable, of } from 'rxjs';
import { Residence } from '../../model/residence';
import { Company } from '../../model/company';
import { AuthorizationService } from '../../service/authorizationService';
import { DeliveryPackageService } from '../../service/delivery-package.service';
import { DataNavigation } from '../../util/dataNavigation';
import { CompanyService } from '../../service/company.service';
import { ResidencesService } from '../../service/residences.service';
import { Visited } from '../../model/visited';
import { _isNumberValue } from '@angular/cdk/coercion';
import { catchError, map } from 'rxjs/operators';
import { AuthRoles } from '../../model/AuthRoles';
import { ProfileComponent } from '../../pages/profile/profile.component';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'deliver-order',
  templateUrl: './deliver-order.component.html',
  styleUrls: ['./deliver-order.component.css']
})
export class DeliverOrderComponent implements OnInit {

  @Input() deliveryPackage: DeliveryPackage

  public registerForm: FormGroup;
  public destination = new FormControl('');
  public visited = new FormControl('');
  public companies: Company[] = [];
  public residences: Residence[] = [];
  public allVisiteds: Visited[] = [];
  public destinationsFields: string[] = [''];
  public filteredDestinations$: Observable<string[]>;
  public visitedFields: string[] = [''];
  public filteredVisiteds$: Observable<string[]>;
  public deliveredByQrcode: boolean = false;
  public loading: boolean = false;
  public deliveryCompleted: boolean = false;

  constructor(private authorizationService: AuthorizationService,
    private deliveryPackageService: DeliveryPackageService,
    private companyService: CompanyService,
    private residencesService: ResidencesService,
    private dataNavigation: DataNavigation,
    public toastService: NbToastrService,
    private translate: TranslateService,
    protected ref: NbDialogRef<ProfileComponent>
  ) {}

  ngOnInit(): void {
    this.registerForm = new FormGroup({
      destination: this.destination,
      visited: this.visited
    });

    // obtemos todos os residentes e colaboradores
    this.getUserAuthorizationsResidents()

    // obtemos todas as unidades
    this.loadData().subscribe((data) => {

      this.residences = data.residences
      this.companies = data.companies

      this.destinationsFields = this.residences.map(r => r.destinationString).concat(this.companies.map(c => c.destinationString));
      this.filteredDestinations$ = of(this.destinationsFields);
      this.visitedFields = this.allVisiteds.map(v => v.name);
      this.filteredVisiteds$ = of(this.visitedFields);

    },
      (err) => {
        console.log(err);
      });

    // fazemos uma assinatura em destination e visited, se hover uma alteração nos campos fazemos a busca
    this.registerFieldsChangesTrigger();
  };

  registerFieldsChangesTrigger() {
    this.destination.valueChanges.subscribe((inputFilter) => {
      // Adiciona no input da Unidade de acordo que vai digitando
      this.filteredDestinations$ = of(this.getFilteredOptions(inputFilter, this.destinationsFields, this.destination));

      // Adiciona todos os residentes de uma residencia selecionada
      this.fillInVisitedFields(this.destinationsFields.some(optionValue => optionValue == inputFilter) ? inputFilter : undefined);
    });

    this.visited.valueChanges.subscribe((inputFilter) => {
      // Preenche no input Nome o residente de acordo que vai digitando
      this.filteredVisiteds$ = of(this.getFilteredOptions(inputFilter, this.visitedFields, this.visited, true));

      // Adiciona as unidades apartir do Nome digitado no input Nome
      if (this.visitedFields.some(optionValue => optionValue == inputFilter)) this.fillInDestinationFieldsByVisited(inputFilter);
    });
  }

  getFilteredOptions(inputFilter: string, options: string[],
    errorForUnmatchedValues?: FormControl, emptyIsValid: boolean = false): string[] {
    if (options.some(optionValue => optionValue == inputFilter)
      || (emptyIsValid && this.isNullOrEmpty(inputFilter))) {
      if (errorForUnmatchedValues != undefined) errorForUnmatchedValues.setErrors(null);
      return options;
    }
    let matchValues = this.isNullOrEmpty(inputFilter) ? options
      : options.filter(optionValue => optionValue.toLowerCase().includes(inputFilter.toLowerCase()));
    if (errorForUnmatchedValues != undefined) errorForUnmatchedValues.setErrors({ 'incorrect': true });
    return matchValues;
  }

  fillInVisitedFields(newDestination?) {
    let residence = this.residences.find(r => r.destinationString == newDestination);
    let company = this.companies.find(c => c.destinationString == newDestination);
    if (residence != undefined) {
      this.visitedFields = this.allVisiteds.filter(v => v.residenceIds.includes(residence.id)).map(v => v.name);
    }
    else if (company != undefined) {
      this.visitedFields = this.allVisiteds.filter(v => v.companyIds.includes(company.id)).map(v => v.name);
    }
    else {
      this.visitedFields = this.allVisiteds.map(v => v.name);
    }
    this.filteredVisiteds$ = of(this.visitedFields);

    if (newDestination != undefined &&
      this.destination.value != '' &&
      this.visited.value != '') {
      if (!this.visitedFields.some(v => v == this.visited.value)) {
        this.visited.setValue('');
      }
    }
  }

  // Adiciona as unidades apartir do Nome digitado no input Nome
  fillInDestinationFieldsByVisited(newVisited) {
    let destinationsOfVisited: string[] = [];

    let visited = this.allVisiteds.find(v => v.name == newVisited);
    if (visited != undefined && visited.residenceIds.length > 0) {
      this.residences.forEach(r => {
        if (visited.residenceIds.includes(r.id)) {
          destinationsOfVisited.push(r.destinationString);
        }
      });
    }

    if (visited != undefined && visited.companyIds.length > 0) {
      this.companies.forEach(r => {
        if (visited.companyIds.includes(r.id)) {
          destinationsOfVisited.push(r.destinationString);
        }
      });
    }

    if (destinationsOfVisited.length > 0) {
      this.filteredDestinations$ = of(destinationsOfVisited);
    }
  }

  loadData() {
    return forkJoin([this.retreiveUserDestinations(), this.retreiveUserAuthorizers()]).pipe(
      map(([destinations]) => {
        return { residences: destinations.residences, companies: destinations.companies }
      })
    );
  }

  retreiveUserDestinations() {
    return forkJoin([this.residencesService.getAll(), this.companyService.getAll()]).pipe(
      map(([residencesResp, companiesResp]) => {
        let residences: Residence[] = residencesResp.items;
        let companies: Company[] = companiesResp.items;
        return { residences, companies };
      }),
      catchError((err) => {
        console.log('Error on retreiveUserDestinations');
        throw err;
      })
    );
  }

  getUserAuthorizationsResidents() {
    return this.authorizationService.getByRole(AuthRoles.resident, false).subscribe((data) => {
      data.items.forEach(v => {
        let residenceIds = v.residenceIds.filter(ri => this.dataNavigation.isUnitAllowed(ri));
        if (residenceIds.length > 0) {
          v.residenceIds = residenceIds;
          this.allVisiteds.push(v);
        }
      });
    }, (err: HttpErrorResponse) => {
      console.log('error', err);
    }, () => this.getUsersAuthorizationsEmployee());
  };

  private getUsersAuthorizationsEmployee() {
    this.authorizationService.getByRole(AuthRoles.employee, false).subscribe((data) => {
      data.items.forEach(v => {
        let companyIds = v.companyIds.filter(ci => this.dataNavigation.isUnitAllowed(ci));
        if (companyIds.length > 0) {
          v.companyIds = companyIds;
          this.allVisiteds.push(v);
        }
      });
    }, (err: HttpErrorResponse) => {
      console.log('error', err);
    }, () => {
      // filtramos por nome e adicionamos os nomes na lista
      this.visitedFields = this.allVisiteds.map(v => v.name);
      this.filteredVisiteds$ = of(this.visitedFields);
    });
  };

  retreiveUserAuthorizers() {
    return this.authorizationService.getAllUnitResponsables().pipe(
      map((data: any) => {
        let userAuthIds = data.items;
        let allVisiteds: Visited[] = [];
        userAuthIds.forEach(a => {
          let existentVisited = allVisiteds.find(v => v.id == a.ownerId);
          if (existentVisited != undefined) {
            existentVisited.addRelatedUnit(a.residenceId, a.companyId);
          }
          else {
            allVisiteds.push(new Visited(a.ownerId, a.ownerName, a.intercomNumber, a.residenceId, a.companyId));
          }
        });
        return allVisiteds;
      }));
  }

  isNullOrEmpty(value: string): boolean {
    return value == null || value == '';
  }


  public deliveredManualClicked() {
    if (this.visited.value == '' || this.visited == undefined){
      this.toastService.show('', this.translate.instant("DELIVERY_PACKAGE.PAGE.DELIVER_ORDER.ALERTS.NAME_REQUIRED"), { status: 'warning', icon: 'cube-outline', duration: 5000 });
      return;
    }
    const visitedId = this.allVisiteds.filter(id => id.name == this.visited.value).map(id => id.id).toString();
    console.log(visitedId);
    console.log(this.deliveryPackage.id)

    this.deliveryPackageService.postStatusDeliveryPackage(this.deliveryPackage.id.toString(), visitedId, this.deliveredByQrcode).subscribe((resp) => {
      console.log(resp);

    }, (error) => {
      console.log("Erro ao tentar dar baixa na encomenda: ", error);

    }, () => {
      this.toastService.show('', this.translate.instant("DELIVERY_PACKAGE.PAGE.DELIVER_ORDER.ALERTS.ORDER_WAS_DELIVERED_TO") + ': ' + this.visited.value, { status: 'success', icon: 'cube-outline', duration: 5000 });
      this.deliveryCompleted = true;
      this.cancelClicked();
    })

  };

  public cancelClicked() {
    this.ref.close(this.deliveryCompleted);
  }

}
